/**
 * Dashboard Stats Section Component
 * Displays animated statistics cards for the VPE Dashboard
 * Extracted from VPEDashboard.tsx for better modularity
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Users, Activity, Target } from 'lucide-react';
import { DashboardStats } from '@/services/ea/vpeDashboardService';

// Counter animation component
const AnimatedCounter = ({ value, duration = 2000 }: { value: number; duration?: number }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      setCount(Math.floor(progress * value));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [value, duration]);

  return <span>{count}</span>;
};

interface DashboardStatsSectionProps {
  stats: DashboardStats;
}

const DashboardStatsSection: React.FC<DashboardStatsSectionProps> = ({ stats }) => {
   return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-testid="ea-dashboard-stats">
      {/* Total Groups Card - Blue theme */}
      <Card className="bg-gradient-to-br from-blue-400 via-blue-500 to-cyan-500 text-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 rounded-2xl overflow-hidden" data-testid="ea-stats-total-groups">
        <CardContent className="p-8 relative">
          <div className="absolute top-4 right-4">
            <Users className="h-8 w-8 text-white/70" />
          </div>
          <div className="flex flex-col">
            <div className="text-4xl font-bold mb-2 tracking-tight" data-testid="ea-stats-total-groups-count">
              <AnimatedCounter value={stats.totalGroups} />
            </div>
            <p className="text-white/90 font-semibold text-lg mb-1">Total Groups</p>
            <p className="text-white/70 text-sm">All patient groups</p>
          </div>
          <div className="absolute bottom-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-8 -mb-8" />
        </CardContent>
      </Card>

      {/* Active Groups Card - Emerald theme */}
      <Card className="bg-gradient-to-br from-emerald-400 via-emerald-500 to-teal-500 text-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 rounded-2xl overflow-hidden" data-testid="ea-stats-active-groups">
        <CardContent className="p-8 relative">
          <div className="absolute top-4 right-4">
            <Activity className="h-8 w-8 text-white/70" />
          </div>
          <div className="flex flex-col">
            <div className="text-4xl font-bold mb-2 tracking-tight" data-testid="ea-stats-active-groups-count">
              <AnimatedCounter value={stats.activeGroups} />
            </div>
            <p className="text-white/90 font-semibold text-lg mb-1">Active Groups</p>
            <p className="text-white/70 text-sm">Currently active</p>
          </div>
          <div className="absolute bottom-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-8 -mb-8" />
        </CardContent>
      </Card>

      {/* Number of Patients Found Card - Amber theme */}
      <Card className="bg-gradient-to-br from-amber-400 via-orange-400 to-red-400 text-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 rounded-2xl overflow-hidden" data-testid="ea-stats-total-patients">
        <CardContent className="p-8 relative">
          <div className="absolute top-4 right-4">
            <Users className="h-8 w-8 text-white/70" />
          </div>
          <div className="flex flex-col">
            <div className="text-4xl font-bold mb-2 tracking-tight" data-testid="ea-stats-total-patients-count">
              <AnimatedCounter value={stats.totalPatients} />
            </div>
            <p className="text-white/90 font-semibold text-lg mb-1">Patients Found</p>
            <p className="text-white/70 text-sm">Total enrolled patients</p>
          </div>
          <div className="absolute bottom-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-8 -mb-8" />
        </CardContent>
      </Card>

      {/* Total VPEs Card - Purple theme */}
      <Card className="bg-gradient-to-br from-purple-400 via-purple-500 to-indigo-500 text-white border-0 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 rounded-2xl overflow-hidden" data-testid="ea-stats-total-vpes">
        <CardContent className="p-8 relative">
          <div className="absolute top-4 right-4">
            <Target className="h-8 w-8 text-white/70" />
          </div>
          <div className="flex flex-col">
            <div className="text-4xl font-bold mb-2 tracking-tight" data-testid="ea-stats-total-vpes-count">
              <AnimatedCounter value={stats.totalVPEs} />
            </div>
            <p className="text-white/90 font-semibold text-lg mb-1">Total VPEs</p>
            <p className="text-white/70 text-sm">Enrolled specialists</p>
          </div>
          <div className="absolute bottom-0 right-0 w-24 h-24 bg-white/10 rounded-full -mr-8 -mb-8" />
        </CardContent>
      </Card>
    </div>
  );
};

export default DashboardStatsSection;